import { BULLETHEAD_SPRITES } from '../ui/data/sprites';
import { PALENOSE_SPRITES } from '../ui/data/palenoseSprites';
import { REEF_FISH_SPRITES } from '../ui/data/reefFishSprites';
import type { CountedReefFishId, FishKind } from './fishSchools';
import type { FishSpeciesId } from '../sim/fish';
import { windowShare, type Swim } from './fishSwim';

/**
 * Each species' swimming as the aquarium draws it (M83), each figure with its
 * source as read. The palenose (M82) swims too, drawn from the user's pictures.
 */

/**
 * The bullethead parrotfish, *Chlorurus spilurus* (as *C. sordidus* in the
 * older Hawaiian sources).
 *
 * - **Its commute**, MEASURED by acoustic telemetry at Kealakekua Bay,
 *   Hawaiʻi Island (10 tagged, 6 ever heard, receivers heard to 250 m):
 *   fish "site-attached to nighttime habitat" for up to 6 months "made daily
 *   crepuscular migrations of 500 to 1,800 m between separate day & night
 *   habitats", and "crossed the NW MPA boundary at sunrise and sunset each
 *   each day" (Meyer 2006, NOAA Coral Grant NA05NMF4631041 final report, p. 1
 *   and printed p. 6; public domain). The range is given for bullethead and
 *   sleek unicornfish together.
 * - **Its day**: "no parrotfish bite sounds were detected before sunrise or
 *   after sunset" at Puakō, Hawaiʻi Island, where the bullethead is the
 *   commonest parrotfish (Tricas and Boyle 2021, Mar Ecol Prog Ser 666, p.
 *   156); so it feeds at its ground by daylight and shelters through the
 *   night, each swim halfway at sunrise and at sunset (JUDGED from Meyer's
 *   fish crossing the boundary then).
 * - **How long it swims**: no source read gives its speed; Meyer's fish
 *   commuted about sunrise and sunset. Drawn making its way out and back
 *   over the whole day, at its ground at noon and its shelter at midnight,
 *   halfway at sunrise and at sunset (`dayShare`; the user's word, M91: on
 *   the game's clock an hour is a fifth of a second, and an hour's swim each
 *   way read as the schools popping between shelter and ground).
 * - **Its company**: "Adults of all Scarus species may form small, mixed
 *   aggregations but adult terminal male S. sordidus [...] are usually
 *   solitary in their habits" (Brock 1979, PhD dissertation, University of
 *   Washington, printed p. 104); its large terminal males "establish feeding
 *   territories with female harems along the reef slope" (Tricas and Boyle
 *   2014, Mar Ecol Prog Ser 511, p. 8). The initial phase in schools of its
 *   own kind (the user's word, M88), 12 sprites to a school (JUDGED: a
 *   sprite stands for 625 fish, so no measured group's size can be drawn;
 *   twelve read as a school at the opening view); its terminal males each
 *   patrolling alone round a school (`schooling.ts`).
 * - **How far it is drawn**: five times its commute (JUDGED, the user's
 *   choice of enlarged wandering: at the opening view a kilometer of reef is
 *   a few pixels).
 */
export const BULLETHEAD_SWIM: Swim = {
  commuteM: [500, 1800],
  wander: 5,
  school: 12,
};

/**
 * The palenose parrotfish, *Scarus psittacus* (as *S. taeniurus* in Hobson's
 * Kona studies; Hobson 1974, p. 995, and Randall 1980, p. 213, on the names).
 *
 * - **Its range**: no distance it swims was read. A palenose tagged at
 *   Waikīkī was caught again 12 days on, 0 m from where it was let go (Meyer
 *   2003, PhD dissertation, University of Hawaiʻi, Table 2.5, p. 45; n = 1),
 *   and Annandale et al. 2024's home ranges are of fish twice its known
 *   length, so not used. 200 m each way (JUDGED: a smaller fish's beat, a
 *   ninth of the bullethead's farthest), drawn five times over as the
 *   bullethead's is, 1 km, so it is seen to move.
 * - **Its day**: it "grazes in exposed positions during daylight, but rests
 *   in coral crevices at night, often encased in a mucous envelope" (Hobson
 *   1972, Fishery Bulletin 70, p. 730); out and home as the bullethead (the
 *   smaller parrotfishes come out later and shelter earlier than the large,
 *   Hobson 1972, pp. 722 and 727, by minutes no source gives for it alone).
 * - **Its company**: "The smaller juveniles and females, usually in
 *   aggregations, tend to occupy the shallow flats, the larger, distinctively
 *   hued males, which are usually solitary, tend to occupy the reef faces"
 *   (Hobson 1974, Fishery Bulletin 72, p. 996); "Initial-phase fish often form
 *   feeding aggregations" (Randall, Snorkeler's Guide to the Fishes of Hanauma
 *   Bay, UH Sea Grant, p. 47); its adult males at Puakō with "completely
 *   non-overlapping core use areas in 5 of the 6 possible combinations"
 *   (Annandale et al. 2024, Front Fish Sci 2, p. 8), as "Many parrotfish
 *   species exhibit haremic social systems wherein adult terminal phase males
 *   actively patrol and defend welldefined territories containing conspecific
 *   initial phase females" (the same, p. 8, after Mumby and Wabnitz). In
 *   schools of 12 as the bullethead (JUDGED), its terminal males patrolling
 *   round them.
 * - **Its sprites**: the user's (`art/palenose/`).
 */
const PALENOSE_SWIM: Swim = {
  commuteM: [200, 200],
  wander: 5,
  school: 12,
};

/**
 * The redlip parrotfish, *Scarus rubroviolaceus* (M155; Maui Nui's since M128), as the sources read for it give it.
 *
 * - **Its range**: "Daytime home ranges (95% KUD) of individual ember parrotfish ranged from 6,392 to 10,914 m2
 *   (mean: 8,712 m2 ± 1,991 m² SD)" (Annandale et al. 2024, *Front. Fish Sci.* 2, p. 5; 4 initial-phase fish tracked
 *   at Puakō, Hawaiʻi Island, by day), its night resting places "consistently within daytime home ranges" (p. 7), and
 *   its detections peaking at dawn and dusk, if "to a lesser extent for ember parrotfish" than the palenose, "linked to
 *   movements between shallower daytime (foraging) and nighttime (resting) areas" (p. 1), its depth by day "more
 *   consistent throughout the entire day" than the palenose's (p. 5). So it goes out and home within its own range: 90
 *   to 118 m, the diameters of circles of the four fish's ranges (MEASURED from the paper's Table 1, p. 4), an upper
 *   bound on a commute within one, drawn five times over as the other parrotfish are, so it is seen to move. Drawn on
 *   the exhibit's and the aquarium's 1 km cells, a commute is whole steps to the next cell, each 1 or 1.41 km
 *   (`reefPath`): its 450 to 590 m is drawn as one, 1 to 1.41 km, the same as the palenose's 1 km (M155, filed; the
 *   Reef's habitat draws its own 4 m on 2 m cells). Howard et al. 2013 (*Mar. Biol.* 160), the
 *   species' own tracking, could not be read (its host refused, M155).
 * - **Its day**: out and home as the bullethead: the largest parrotfishes are the first out ("S. perspicillatus or
 *   S. rubroviolaceus, the two largest species, were seen first in five of the nine instances", Hobson 1972, *Fishery
 *   Bulletin* 70, p. 727) and go under cover "at about the time that the water column is vacated" (p. 721), "about
 *   10 to 15 min after sunset" (the same); "At night S. rubroviolaceus rests in reef crevices" (Hobson 1974, *Fishery
 *   Bulletin* 72, p. 996), by minutes the bullethead's day already holds.
 * - **Its company**: "During the day this parrotfish ranges over the reef, usually in mixed groups of several males
 *   and females" (Hobson 1974, p. 996), at dawn as "individuals, or groups of two or three fish" (Hobson 1972, p. 727):
 *   in schools of 3 (JUDGED: "several", a sprite standing for many fish), its terminal males about them.
 * - **Its sprites**: the user's (`art/redlip/`, M155).
 */
const REDLIP_SWIM: Swim = {
  commuteM: [90, 118],
  wander: 5,
  school: 3,
};

/** A day's sunrise and sunset, local solar hours, for a day `dayLengthHours` long. */
const sunriseOf = (dayLengthHours: number): number => 12 - dayLengthHours / 2;

/**
 * The roi, the peacock grouper *Cephalopholis argus* (M97; Maui Nui's predator since M84).
 *
 * - **Its day**, MEASURED by acoustic telemetry off Kona (n = 6): "fish were active during the day and
 *   quiescent at night C. argus moved very little, if at all, during the hours from 19:00 to 05:30. Transmitter
 *   signals were present on receivers during the daylight hours starting around 06:00, shortly after sunrise,
 *   and ending around 19:00, sunset" (Meyer, A.L. 2008, PhD dissertation, University of Hawaiʻi, p. 74); "As
 *   sunset approached, C. argus would settle into core areas" (p. 84). Drawn awake from sunrise to half an hour
 *   after it, settling over the last hour before sunset (JUDGED from those words), resting low and dim at its
 *   shelter through the night.
 * - **Its range**: "highly site attached, patrolling home ranges by day and rarely moving at night. Home range
 *   size ... 425 to 2300 m 2 , mean = 1236 m 2 in Hawaii" (the same, abstract): no commute, a fish milling
 *   about its own point of the reef (a range about 40 m across, two of the exhibit's steps at its shore).
 * - **Its company**: "This solitary fish" (Hobson 1974, *Fishery Bulletin* 72, p. 947): a sprite alone.
 * - **Its sprite**: the user's (`art/roi/`, M135; drawn in code from M97).
 */
const ROI_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  /** Its home range's mean, "mean = 1236 m 2 in Hawaii" (above): within a disc of it about its shelter it roams the Reef by day (M159). */
  homeRangeM2: 1236,
  school: 1,
  /** A predator, no grazer: no feeding dives (M265, the interactions brief: "Do not use herbivore-style grazing animations for roi"; its school had dived with the grazers', M159). */
  grazes: false,
  day: {
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 0, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      return { out: 0, awake: windowShare(hour, rise, rise + 0.5) * (1 - windowShare(hour, set - 1, set)) };
    },
    easeS: 2,
  },
};

/**
 * The kala, the bluespine unicornfish *Naso unicornis* (M97; Maui Nui's since M90).
 *
 * - **Its commute**, MEASURED by acoustic tracking at Waikīkī (n = 5): "Highly directional movements between day
 *   and night habitats occurred within 15-30 minutes of sunset and sunrise each day" (Meyer, C.G. 2003, PhD
 *   dissertation, University of Hawaiʻi, p. 12); one fish "swam steadily and directly north along the reef crest
 *   to a nighttime refuge hole situated 370 m away", another "moved steadily 300 m north" at sunset (pp. 14-15);
 *   one left its night ground "at around 05:30 each morning ... taking 45 minutes to complete the journey and
 *   arriving at around sunrise" (p. 14). Drawn leaving its shelter three quarters of an hour before sunrise and
 *   at its day ground by sunrise, leaving at sunset and home half an hour after (JUDGED from those words), its
 *   commute 300 to 370 m, drawn five times over as the parrotfish's.
 * - **Its night**: "Unicornfishes were typically most active during the day and quiescent in refuge holes at
 *   night" (the same, p. 12).
 * - **Its company**: "Schools consisted of 5 - 100 N. unicornis" (p. 14), by day: 6 sprites to a school (JUDGED:
 *   a sprite stands for 625 fish, so no measured school can be drawn as it is).
 * - **Its sprite**: the user's (`art/kala/`, M136; drawn in code from M97).
 * - It cruises two of its lengths over the reef (M169, the v4 brief's "unicornfish could cruise across the reef";
 *   JUDGED), dropping to browse as the parrotfish do.
 */
const KALA_SWIM: Swim = {
  commuteM: [300, 370],
  wander: 5,
  school: 6,
  cruiseBL: 2,
  day: {
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 1, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      const out = windowShare(hour, rise - 0.75, rise) * (1 - windowShare(hour, set, set + 0.5));
      return { out, awake: out };
    },
    easeS: 2,
  },
};

/**
 * The ulua, the giant trevally *Caranx ignobilis* (M146; Maui Nui's since M126).
 *
 * - **Its range**: "Ulua showed moderate to high site fidelity to small home range areas typically 3.8 to 5.4 km in
 *   maximum linear dimension" (Evans 2021, MS thesis, University of Hawaiʻi, abstract, p. iii): the 3.8 to 5.3 km where
 *   the detections of 166 tagged fish fall off with distance from where each was caught (pp. 25 and 40, MEASURED, the
 *   curve's bend), and 4.9 and 5.4 km for two fish tracked by boat off Hawaiʻi Island (p. 29, MEASURED). Its way that long,
 *   drawn once its length: in the whole tank, a kilometer of coast a column, 4 to 6 columns, its way as drawn 0.73 to 2.33
 *   units along the reef (MEASURED on a new world's 44). At Molokini, inside Maui Nui, ulua "frequently made large-scale movements ranging from 5 to 11
 *   km between the MLCD and receivers located throughout the Maui Nui complex" (Filous 2016, MS thesis, University of
 *   Hawaiʻi, p. 10; 8 of 16 fish): not drawn, nor the summer's longer trips to spawn.
 * - **Its day**: "Giant trevally at remote NWHI atolls are site-attached to home ranges where they swim back and forth
 *   daily between separate day and night habitats" (the Hawaiʻi Institute of Marine Biology's project page, "Giant
 *   Trevally Research", read 20 October 2026, a summary of its lab's tracking): drawn out and back over the whole day
 *   as the parrotfish are (`dayShare`), as no source read gives its hours, and awake at both grounds
 *   (`awakeAtNight`, M146's review): its prey "suggests at least partly nocturnal feeding habits", and Okamoto and
 *   Kawamoto found it "appeared to be primarily a night feeder", and Van der Elst "more active during the day,
 *   especially at dusk and dawn" (all Sudekum et al. 1991, *Fishery Bulletin* 89, p. 506; awake by day and by night the
 *   reading that holds all of it, JUDGED);
 *   at Molokini "The majority of detections from this species occurred during daylight hours" (Filous, p. 10), though
 *   "it is unclear to what extent these absences are a result of movements out of the MLCD or reduced detection
 *   efficiency" at night (p. 12).
 * - **Its company**: "disproportionate numbers of gravid females were taken from large schools during summer"
 *   (Sudekum et al., p. 506), with no size; no other group of grown ulua was read: a sprite alone (JUDGED).
 * - It cruises three of its lengths over the reef and never drops to it in beats (M169, the v4 brief's "larger trevally
 *   could occasionally pass through the water column"; JUDGED: how it hunts is unread; before, it dropped as the
 *   parrotfish do).
 * - **Its sprite**: the user's (`art/ulua/`, M146).
 */
const ULUA_SWIM: Swim = {
  commuteM: [3800, 5400],
  wander: 1,
  school: 1,
  awakeAtNight: true,
  cruiseBL: 3,
  grazes: false,
};

/**
 * The ʻōmilu, the bluefin trevally *Caranx melampygus* (M146; Maui Nui's since M126).
 *
 * - **Its day**: at Molokini, inside Maui Nui, "Omilu are primarily active in subzone A of the MLCD during day light
 *   hours and on average were present during 77% of the days they were monitored" (Friedlander, Filous and Sparks,
 *   Hawaiʻi Division of Aquatic Resources' poster "Reef Predator Movements at the Molokini Marine Life Conservation
 *   District", acoustic tags, 2013 to 2015, MEASURED); "Potts described C. melampygus as a diurnally active predator that
 *   increased hunting activity at dawn and dusk" (Sudekum et al. 1991, *Fishery Bulletin* 89, p. 508, Potts's at Aldabra,
 *   compiled). Drawn awake from half an hour before sunrise to half an hour after sunset (JUDGED from those words, so its
 *   dawn and dusk are its own), resting low and dim through the night as the roi.
 * - **Its range**: no distance it swims was read (Holland, Lowe and Wetherbee 1996, *Fisheries Research* 25, tracked it
 *   at Kāneʻohe Bay, on a host the environment refuses): no commute, a pair milling about its own point of the reef as
 *   the roi does about its own.
 * - **Its company**: "this species sometimes forages in small groups, but that it is most often found singly or in
 *   pairs" (Sudekum et al., p. 508, their words for Potts's): two sprites together (JUDGED, the pair).
 * - It cruises two and a half of its lengths over the reef and never drops to it in beats (M169, as the ulua; JUDGED).
 * - **Its sprite**: the user's (`art/omilu/`, M146).
 */
const OMILU_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 2,
  cruiseBL: 2.5,
  grazes: false,
  day: {
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 0, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      return { out: 0, awake: windowShare(hour, rise - 0.5, rise) * (1 - windowShare(hour, set, set + 0.5)) };
    },
    easeS: 2,
  },
};

/**
 * The nenue, the sea chubs *Kyphosus* (M185; Maui Nui's since M178), drawn as Hobson saw *K. cinerascens* at Kona
 * (Hobson 1974, *Fishery Bulletin* 72, p. 964, divers' observations).
 *
 * - **Its day**: "active throughout the day-usually in groups of up to 10 or more individuals", and "Sea chubs generally
 *   are described as diurnal herbivores"; at night "solitary individuals swimming above the sea floor are often
 *   encountered in the same areas", its "nocturnal habits remain uncertain". Drawn awake and feeding from half an
 *   hour before sunrise to half an hour after sunset, the ʻōmilu's hours, resting low and dim through the night (JUDGED: the diurnal
 *   reading; drawn awake at night as the ulua, its lone sprite trailed its roaming middle by 3.07 of its lengths at 9
 *   am, MEASURED, `tests/reefFishMotion.test.ts`, against every other kind's 2.31 at most).
 * - **Its range**: none read (no tracking of a Hawaiian chub was found): no commute, a group milling about its own point
 *   of the reef, as the ʻōmilu (JUDGED).
 * - **Its company**: groups of "up to 10 or more": three sprites together (JUDGED: a sprite stands for 625 fish, so no
 *   group can be drawn as it is; three reads as a group, fewer than the kala's six for its schools of 5 to 100).
 * - **Its height**: "often swimming high in the water column": it cruises two and a half of its lengths over the reef, as
 *   the ʻōmilu, higher than the parrotfish and the kala and below the ulua, which nothing read says it swims above
 *   (JUDGED from those words), and drops to it to bite, its guts "full of a wide variety of benthic algae", bitten
 *   "off the rocks, or else ... taken as fragments drifting in mid-water".
 * - **Its sprite**: the user's (`art/nenue/`, M185).
 */
const NENUE_SWIM: Swim & { day: NonNullable<Swim['day']> } = {
  commuteM: [0, 0],
  wander: 1,
  school: 3,
  cruiseBL: 2.5,
  day: {
    // Out at its feeding ground (there being no route, the reef where it mills) as it is awake, so it drops to bite there
    // (M185's review: with the ʻōmilu's day, never out, it never fed).
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 1, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      const awake = windowShare(hour, rise - 0.5, rise) * (1 - windowShare(hour, set, set + 0.5));
      return { out: awake, awake };
    },
    easeS: 2,
  },
};

/**
 * The moano kea, the blue goatfish *Parupeneus cyclostomus* (M198; Maui Nui's since M179), drawn as Hobson saw it at
 * Kona as *P. chryserydros* (Hobson 1974, *Fishery Bulletin* 72, pp. 962 and 963, divers' observations over 632 hours at
 * every hour, and 20 fish speared).
 *
 * - **Its day**: "During the day, solitary individuals or groups of two to five move over the reef"; "After dark, P.
 *   chryserydros is inactive, resting under reef cover"; 15 of the 17 speared by day ("between midmorning and late
 *   afternoon") had prey in their stomachs, the 3 speared at rest at night none (MEASURED); "a diurnal predator that feeds
 *   mostly on small fishes". Drawn awake and hunting from half an hour before sunrise to half an hour after sunset, the
 *   ʻōmilu's and the nenue's hours, resting low and dim through the night (JUDGED: its dawn and dusk unread).
 * - **Its range**: none read (no tracking of the species was found): no commute, a group milling about its own point of
 *   the reef, as the ʻōmilu (JUDGED).
 * - **Its company**: alone or "groups of two to five": three sprites together (JUDGED, their middle). Of 24 groups Hobson
 *   counted in a month, 16 had an ʻōmilu following close behind (MEASURED): not drawn, the ʻōmilu's pairs keep to their own
 *   patch.
 * - **Its height**: "their exceptionally long barbels work through the covering on rocky substrata", taken as they "swam
 *   close to the reef": it cruises one of its lengths over the reef, lower than the parrotfish's 1.5 (JUDGED from those
 *   words), and drops to it in beats to probe (`grazes`).
 * - **Its sprites**: the user's (`art/blue-goatfish/`, M189), its usual phase. Its all-yellow phase is read but not
 *   drawn: no count of how many are yellow was read (Randall's accounts and Strübin et al. 2011 unreachable here).
 */
const MOANOKEA_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 3,
  cruiseBL: 1,
  // The nenue's day, its hours the same: out at its feeding ground (there being no route, the reef where it mills) as
  // it is awake, so it drops to probe there.
  day: NENUE_SWIM.day,
};

/**
 * The yellow tang, the lauʻīpala *Zebrasoma flavescens* (M231; counted at Maui Nui since M217, not simulated), drawn as
 * Hobson saw it and the surgeonfishes at Kona (Hobson 1972, *Fishery Bulletin* 70: 715, divers at 20 dusks and 18 dawns;
 * Hobson 1974, *Fishery Bulletin* 72: 915; both public domain).
 *
 * - **Its day**: surgeonfishes are "active by day and relatively inactive at night" (Hobson 1974, p. 1003); at night a
 *   tang "nestled among the coral", "the horizontal white bar on its side is, in adults, a feature of its nocturnal
 *   coloration" (Hobson 1972, Fig. 6, p. 724; one sprite, its night colors not drawn). At dawn "when sunrise is still
 *   about 25 min away" the surgeonfishes "begin to assemble in depressions", and "At about 20 min before sunrise" they
 *   "rise 1 to 2 m above the reef" (pp. 726 and 727, timed on 6 mornings); at dusk "5 to 15 min after sunset" mixed
 *   aggregations, "especially Zebrasoma flavescens", "mill about ... 1 or 2 m above the substrate", then "at about 10
 *   to 15 min after sunset ... the vast majority of these fishes suddenly drop to the reef below" (pp. 720 and 721,
 *   timed on 12 evenings; MEASURED). Drawn awake from 25 to 20 minutes before sunrise and to sleep from 10 to 15 after
 *   sunset, at its feeding ground while awake, resting low and dim through the night.
 * - **Its range**: adults migrate daily between "deep coral reef habitats that are used for refuge" and "shallow coral
 *   reef flats used primarily for feeding" (Schemmel 2021, *Environ. Biol. Fish.*, p. 2, a US government work,
 *   compiling Walsh 1984 and Claisse et al. 2011); no distance was read (Claisse et al. 2011 and Walsh 1984 on hosts
 *   refused here): no commute, a group milling about its own point of the reef, as the moano kea (JUDGED).
 * - **Its company**: by day the surgeonfishes swim in groups "smaller, more loosely organized, and occurring closer to
 *   the substrate at many different places on the reef" than their twilight assemblages (Hobson 1972, p. 735, the
 *   family's); no count read: three sprites together, the nenue's and the moano kea's small groups (JUDGED).
 * - **Its height**: "closer to the substrate" by day (above), a browser of "fine filamentous algae" (Hobson 1974, p.
 *   1003, compiling Jones 1968): it cruises one of its lengths over the reef, as the moano kea, and drops to it to
 *   bite (JUDGED from those words).
 * - **Its sprite**: the user's (`art/yellow-tang/`, M231). Those of 10 cm or more drawn, as every simulated species'
 *   (review M231's), at the divers' mean length of them, 12.4 cm (`REEF_SURVEYS`, MEASURED).
 */
const YELLOW_TANG_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 3,
  cruiseBL: 1,
  day: {
    // Out at its feeding ground (there being no route, the reef where it mills) as it is awake, so it drops to bite there.
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 1, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      const awake = windowShare(hour, rise - 25 / 60, rise - 20 / 60) * (1 - windowShare(hour, set + 10 / 60, set + 15 / 60));
      return { out: awake, awake };
    },
    easeS: 2,
  },
};

/**
 * The manini, the convict tang *Acanthurus triostegus sandvicensis* (M232; Maui Nui's stock since M220), drawn by what
 * the readable sources give of it and of the surgeonfishes at Kona (Hobson 1972 and 1974, public domain). Randall 1961,
 * its Hawaiian life history (*Pacific Science* 15: 215), is behind a bot check here, unread (asked of the user).
 *
 * - **Its day**: the surgeonfishes' (`YELLOW_TANG_SWIM`'s): "active by day and relatively inactive at night" (Hobson
 *   1974, pp. 1002 and 1003), and "Most acanthurids on the reef are of about the same size and therefore show similar
 *   timing in seeking cover" (Hobson 1972, p. 734), so drawn on the yellow tang's hours, its twilight timed at Kona.
 * - **Its company and range**: "schools of grazing surgeonfishes-usually mixed groups of Acanthurus sandvicensis and A.
 *   nigroris, which frequently move across the reef" (Hobson 1974, p. 943, its name for the manini; observed, no size
 *   given): six sprites together, the kala's for its schools (JUDGED: schools, not the tang's loose few), roaming the
 *   reef by day as every school does (`REEF_ROAM`); no commute, none measured read.
 * - **Its height**: a browser of "fine filamentous algae" on basalt and coral "below the surge zone" (Hobson 1974, p.
 *   1003, compiling Jones 1968): one of its lengths over the reef, dropping to bite, as the yellow tang (JUDGED).
 * - **Its sprite**: the user's (`art/manini/`, M232).
 */
const MANINI_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 6,
  cruiseBL: 1,
  day: YELLOW_TANG_SWIM.day,
};

/**
 * The taʻape, the bluestripe snapper *Lutjanus kasmira* (M233; Maui Nui's stock since M218), drawn by the readable
 * sources (M233's reader; Friedlander, Parrish and DeFelice 2002 and Schumacher and Parrish 2005, the shallow reef's
 * tagging, behind a bot check and a refused host, unread, asked of the user).
 *
 * - **Its day**: "an active nocturnal carnivore feeding on or near the bottom" (Uchida 1986, NOAA SWFSC, p. 110, a US
 *   government work, compiled); its catch on hooks fished round the clock "high values between ~1600 and 0200 and very
 *   low values between 0400 and 1600" (Parrish et al. 2000, a report to Hawaiʻi's DAR, p. 7, MEASURED by the hour, its
 *   midday sampled thinly, p. 10). Tropical reef fishes that forage at night, lutjanids among them, "characteristically
 *   spend the day in large inactive schools that hover in exposed positions on or near the reef" (Hobson 1972, *Fishery
 *   Bulletin* 70, p. 730, public domain, citing Hobson 1968); Hawaiʻi's other introduced snapper "forms relatively
 *   inactive schools during the day, then disperses at nightfall and hunts prey after dark" (Hobson 1974, *Fishery
 *   Bulletin* 72, p. 956). No timing of its own was read, so Kona's nocturnal fishes': their emergence "from about 33
 *   to 50 min after sunset" (Hobson 1972, p. 739, its summary), back under cover "approximately 50 to 30 min before
 *   sunrise" (p. 726). Drawn out from 33 to 50 minutes after sunset to 50 to 30 minutes before sunrise, active; and by
 *   day resting in its school, its pace slowed, roaming nowhere, low over the reef and lit, not sheltering
 *   (`restsInOpen`; the reviewer's finding: awake all day, it roamed the view at noon as at midnight).
 * - **Its company**: by day schools of 15 to about 250 (Parrish et al. 2000, pp. 20 to 25, estimated from a submersible
 *   and an ROV): six sprites together, the kala's, the largest the Reef draws (JUDGED). By night "alone or in groups of
 *   2-3" (p. 33): not drawn, its schools kept whole through the night (the swim's schools never part; JUDGED).
 * - **Its range**: no distance read (the tagging above unread): no commute, as the moano kea's.
 * - **Its height**: about half a meter over the bottom by day (one sighting, p. 21), "within several centimeters of the
 *   bottom" by night (p. 22), its catch "much greater within about 5 m above bottom" (p. 14, MEASURED): one of its
 *   lengths over the reef out, at rest the resting height every kind keeps (JUDGED), and no bites at the reef by day.
 * - **Its sprite**: the user's (`art/taape/`, M233).
 */
const TAAPE_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 6,
  cruiseBL: 1,
  grazes: false,
  restsInOpen: true,
  day: {
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 0, awake: 0 };
      if (dayLengthHours <= 0) return { out: 1, awake: 1 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      // In its school from its morning's cover-seeking to its evening's dispersal, the windows' day turning at midnight
      // (`windowShare`); out the rest.
      const schooled = windowShare(hour, rise - 50 / 60, rise - 30 / 60) * (1 - windowShare(hour, set + 33 / 60, set + 50 / 60));
      return { out: 1 - schooled, awake: 1 - schooled };
    },
    easeS: 2,
  },
};

export const FISH_KINDS: readonly (FishKind & { species: FishSpeciesId })[] = [
  { species: 'bullethead', swim: BULLETHEAD_SWIM, initial: BULLETHEAD_SPRITES.adult, terminal: BULLETHEAD_SPRITES.terminal },
  ...(PALENOSE_SPRITES.adult ? [{ species: 'palenose' as const, swim: PALENOSE_SWIM, initial: PALENOSE_SPRITES.adult, terminal: PALENOSE_SPRITES.terminal }] : []),
  // The roi a sprite for 2,500 (JUDGED, a rendering choice, said on its card: at 625 its 324,308 adults on the first
  // day were 519 sprites, each longer than a parrotfish, and carpeted the reef; at 2,500, 129, each island's rounded).
  { species: 'roi', swim: ROI_SWIM, initial: REEF_FISH_SPRITES.roi, perSprite: 2500 },
  { species: 'kala', swim: KALA_SWIM, initial: REEF_FISH_SPRITES.kala },
  { species: 'ulua', swim: ULUA_SWIM, initial: REEF_FISH_SPRITES.ulua },
  { species: 'omilu', swim: OMILU_SWIM, initial: REEF_FISH_SPRITES.omilu },
  { species: 'redlip', swim: REDLIP_SWIM, initial: REEF_FISH_SPRITES.redlip, terminal: REEF_FISH_SPRITES.redlipTerminal },
  { species: 'nenue', swim: NENUE_SWIM, initial: REEF_FISH_SPRITES.nenue },
  { species: 'moanokea', swim: MOANOKEA_SWIM, initial: REEF_FISH_SPRITES.moanokea },
  { species: 'manini', swim: MANINI_SWIM, initial: REEF_FISH_SPRITES.manini },
  // The taʻape a sprite for 7,500 (M233, JUDGED, a rendering choice: at 625 its adults were 881 of the aquarium's 2,095
  // sprites, more than four times any other kind's; at 2,500, 220 of 1,434, MEASURED). Held to NOAA's divers' count, three
  // times as many (M252), 2,500 drew 731 of 1,945 and the Reef's phone shot 9 to 12 recognizable fish: three times the
  // fish a sprite keeps M233's share.
  { species: 'taape', swim: TAAPE_SWIM, initial: REEF_FISH_SPRITES.taape, perSprite: 7500 },
];

/**
 * The yellowmargin moray, the puhi paka *Gymnothorax flavimarginatus* (M236; counted at Maui Nui since M222, not
 * simulated), drawn by Hobson's divers at Kona (Hobson 1974, *Fishery Bulletin* 72: 915, public domain, p. 927): "the
 * most numerous of the larger muraenids in Kona"; "Most encounters with G. flavimarginata were by day; although its
 * behavior would seem equally adaptive to nocturnal conditions, it was only occasionally observed after dark"; one of the
 * morays that "feed regularly in daylight" (p. 929); "especially sensitive to stimuli emanating from a fish in distress,
 * and appears adapted to seeking out such individuals when they have sought shelter in reef crevices" (p. 927).
 *
 * - **Its day**: out by day, under cover by night, on Kona's diurnal fishes' hours (Hobson 1972, *Fishery Bulletin* 70,
 *   p. 739, its summary): their emergence "from about 13 min before sunrise to a point soon after sunrise", their
 *   shelter-seeking "from about 30 min before sunset to about 13 min after sunset", "the larger fishes seek cover last"
 *   (p. 739): drawn out from 13 minutes before sunrise to sunrise and in from sunset to 13 minutes after (JUDGED, its
 *   "last" and "soon after" read so).
 * - **Its company and range**: alone (one sprite a school); no range read: no commute, roaming the reef as every kind
 *   does by day (`REEF_ROAM`).
 * - **Its height**: along the reef, seeking fish in its crevices: a fifth of its length over it, the height every fish
 *   rests at (JUDGED), no bites by day.
 * - **Its sprite**: the user's (`art/moray/`, M236).
 */
const MORAY_SWIM: Swim = {
  commuteM: [0, 0],
  wander: 1,
  school: 1,
  cruiseBL: 0.2,
  grazes: false,
  day: {
    at: (hour, dayLengthHours) => {
      if (dayLengthHours >= 24) return { out: 1, awake: 1 };
      if (dayLengthHours <= 0) return { out: 0, awake: 0 };
      const rise = sunriseOf(dayLengthHours);
      const set = 24 - rise;
      const awake = windowShare(hour, rise - 13 / 60, rise) * (1 - windowShare(hour, set, set + 13 / 60));
      return { out: awake, awake };
    },
    easeS: 2,
  },
};

/** The reef fish counted, not simulated, as the Reef draws them (M231): the simulation's stocks are `FISH_KINDS`'. */
export const COUNTED_REEF_KINDS: readonly (FishKind & { species: CountedReefFishId })[] = [
  { species: 'yellowTang', swim: YELLOW_TANG_SWIM, initial: REEF_FISH_SPRITES.yellowTang },
  { species: 'moray', swim: MORAY_SWIM, initial: REEF_FISH_SPRITES.moray },
];
